import { useState, useMemo } from 'react';

// Helper function to convert 24h time format (HHMM) to seconds since midnight
const timeToSeconds = (timeString: string): number => {
  const trimmed = timeString.trim();
  if (trimmed.length !== 4) {
    throw new Error(`Invalid time format: ${timeString}`);
  }

  const hours = parseInt(trimmed.substring(0, 2), 10);
  const minutes = parseInt(trimmed.substring(2, 4), 10);

  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    throw new Error(`Invalid time values: ${timeString}`);
  }

  return hours * 3600 + minutes * 60;
};

// Helper function to convert seconds since midnight back to 24h time format (HHMM)
const secondsToTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours.toString().padStart(2, '0') + minutes.toString().padStart(2, '0');
};

export interface FilterKeyConfig {
  key: string;
  type: 'string' | 'number' | 'numberRange' | 'timeRange' | 'boolean' | 'combined' | 'position';
  isArray?: boolean;
  displayName?: string;
  children?: FilterKeyConfig[];
}

export const createInitialFilterState = <T extends Record<string, any>>(
  filterKeys: FilterKeyConfig[]
): T => {
  // Only parse URL on client side to avoid SSR issues
  if (typeof window !== 'undefined') {
    return parseUrlFilters<T>(filterKeys);
  }
  return {} as T;
};

export const parseUrlFilters = <T extends Record<string, any>>(
  filterKeys: FilterKeyConfig[]
): T => {
  const urlParams = new URLSearchParams(window.location.search);
  const filter: Record<string, unknown> = {};

  // Create a lookup map for better performance
  const filterConfigMap = new Map(filterKeys.map(config => [config.key, config]));
  // Also create a reverse lookup for displayName -> config
  const displayNameMap = new Map(
    filterKeys.filter(config => config.displayName).map(config => [config.displayName!, config])
  );

  urlParams.forEach((value, key) => {
    let filterConfig = filterConfigMap.get(key);
    // If not found by key, try to find by displayName
    if (!filterConfig) {
      filterConfig = displayNameMap.get(key);
    }
    if (!filterConfig) {
      return; // Skip keys that aren't in our configuration
    }

    // Handle empty values
    if (!value.trim()) {
      return;
    }

    const shouldBeArray = filterConfig.isArray || value.includes(',');

    if (filterConfig.type === 'string') {
      if (shouldBeArray) {
        const values = value
          .split(',')
          .map(v => v.trim())
          .filter(v => v.length > 0);
        if (values.length > 0) {
          filter[filterConfig.key] = values;
        }
      } else {
        filter[filterConfig.key] = value.trim();
      }
    } else if (filterConfig.type === 'number') {
      if (shouldBeArray) {
        const numbers = value
          .split(',')
          .map(v => v.trim())
          .filter(v => v.length > 0)
          .map(v => parseInt(v, 10))
          .filter(n => !isNaN(n));
        if (numbers.length > 0) {
          filter[filterConfig.key] = numbers;
        }
      } else {
        const numValue = parseInt(value.trim(), 10);
        if (!isNaN(numValue)) {
          filter[filterConfig.key] = numValue;
        }
      }
    } else if (filterConfig.type === 'numberRange') {
      const range = value.split(',');
      if (range.length !== 2) {
        console.warn(`Invalid number range format for key "${key}": ${value}`);
      } else {
        const start = parseInt(range[0].trim(), 10);
        const end = parseInt(range[1].trim(), 10);
        if (isNaN(start) || isNaN(end)) {
          console.warn(`Invalid number values for key "${key}": ${value}`);
        } else {
          filter[filterConfig.key] = { start, end };
        }
      }
    } else if (filterConfig.type === 'timeRange') {
      const range = value.split(',');
      if (range.length !== 2) {
        console.warn(`Invalid time range format for key "${key}": ${value}`);
      } else {
        try {
          const startSeconds = timeToSeconds(range[0]);
          const endSeconds = timeToSeconds(range[1]);
          filter[filterConfig.key] = {
            start: startSeconds,
            end: endSeconds,
          };
        } catch (error) {
          console.warn(`Invalid time format for key "${key}": ${value}`, error);
        }
      }
    } else if (filterConfig.type === 'boolean') {
      if (value.toLowerCase() === 'true') {
        filter[filterConfig.key] = true;
      } else if (value.toLowerCase() === 'false') {
        filter[filterConfig.key] = false;
      } else {
        console.warn(`Invalid boolean value for key "${key}": ${value}`);
      }
    } else if (filterConfig.type === 'position') {
      // Assuming location is a string in the format "lat,lon,alt"
      const parts = value.split(',').map(part => part.trim());
      if (parts.length === 2) {
        const latitude = parseFloat(parts[0]);
        const longitude = parseFloat(parts[1]);
        if (!isNaN(latitude) && !isNaN(longitude)) {
          filter[filterConfig.key] = { latitude, longitude, altitude: null };
        } else {
          console.warn(`Invalid location format for key "${key}": ${value}`);
        }
      }
    } else if (filterConfig.type === 'combined' && filterConfig.children) {
      // Parse the comma-separated values
      const selectedValues = value
        .split(',')
        .map(v => v.trim())
        .filter(v => v.length > 0);

      // Create a map of displayName to child config for quick lookup
      const displayNameMap = new Map(
        filterConfig.children.map(child => [child.displayName, child])
      );

      // Process each selected value and add directly to the root filter
      selectedValues.forEach(selectedValue => {
        const childConfig = displayNameMap.get(selectedValue);
        if (childConfig) {
          // For boolean types, set to true when the displayName is present
          if (childConfig.type === 'boolean') {
            filter[childConfig.key] = true;
          } else {
            // For other types, you might want to handle them differently
            // This is a basic implementation
            filter[childConfig.key] = selectedValue;
          }
        } else {
          console.warn(`Unknown combined filter value "${selectedValue}" for key "${key}"`);
        }
      });
    }
  });

  console.log('Parsed URL filters:', filter);
  return filter as T;
};

export const serializeFiltersToUrl = <T extends Record<string, any>>(
  filters: T,
  filterKeys: FilterKeyConfig[],
  humanReadable = false
): string => {
  // Start with existing URL parameters to preserve non-filter params
  const urlParams = new URLSearchParams(window.location.search);

  // Create a lookup map for filter configurations
  const filterConfigMap = new Map(filterKeys.map(config => [config.key, config]));

  // Get all filter keys that we manage (including combined filter parent keys)
  const managedKeys = new Set(filterKeys.map(config => config.key));

  // Create a reverse map for combined filters (child key to parent)
  const combinedFilterMap = new Map<string, { parentKey: string; displayName: string }>();
  filterKeys.forEach(config => {
    if (config.type === 'combined' && config.children) {
      config.children.forEach(child => {
        if (child.displayName) {
          combinedFilterMap.set(child.key, {
            parentKey: config.key,
            displayName: child.displayName,
          });
        }
      });
    }
  });

  // Clear existing filter parameters to avoid stale values
  managedKeys.forEach(key => {
    // if Key is a displayName, find the actual key
    const config = filterConfigMap.get(key);
    const urlKey = config?.displayName || key;
    urlParams.delete(urlKey);
  });

  // Track which combined filters have been processed
  const processedCombinedFilters = new Set<string>();

  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    // Check if this key belongs to a combined filter's children first —
    // those keys only exist nested under a parent and are never in filterConfigMap.
    const combinedInfo = combinedFilterMap.get(key);
    if (combinedInfo) {
      if (processedCombinedFilters.has(combinedInfo.parentKey)) {
        return; // Already processed this combined filter
      }

      // Collect all active combined filter values
      const combinedConfig = filterConfigMap.get(combinedInfo.parentKey);
      if (combinedConfig?.children) {
        const activeValues: string[] = [];
        combinedConfig.children.forEach(child => {
          if (child.displayName && filters[child.key]) {
            if (child.type === 'boolean' && filters[child.key] === true) {
              activeValues.push(child.displayName);
            } else if (child.type !== 'boolean' && filters[child.key]) {
              activeValues.push(child.displayName);
            }
          }
        });

        if (activeValues.length > 0) {
          const parentConfig = filterConfigMap.get(combinedInfo.parentKey);
          const parentUrlKey = parentConfig?.displayName || combinedInfo.parentKey;
          urlParams.set(parentUrlKey, activeValues.join(','));
        }
        processedCombinedFilters.add(combinedInfo.parentKey);
      }
      return;
    }

    const filterConfig = filterConfigMap.get(key);
    if (!filterConfig) {
      console.warn('No filter config for key', key);
      return; // Skip keys that aren't in our configuration
    }

    // Use displayName for URL if available, otherwise use key
    const urlKey = filterConfig.displayName || key;

    if (filterConfig.type === 'string') {
      if (Array.isArray(value)) {
        if (value.length > 0) {
          urlParams.set(urlKey, value.join(','));
        }
      } else if (typeof value === 'string' && value.trim()) {
        urlParams.set(urlKey, value.trim());
      }
    } else if (filterConfig.type === 'number') {
      if (Array.isArray(value)) {
        const validNumbers = value.filter(v => typeof v === 'number' && !isNaN(v));
        if (validNumbers.length > 0) {
          urlParams.set(urlKey, validNumbers.join(','));
        }
      } else if (typeof value === 'number' && !isNaN(value)) {
        urlParams.set(urlKey, value.toString());
      }
    } else if (filterConfig.type === 'timeRange') {
      if (value && typeof value === 'object' && 'start' in value && 'end' in value) {
        const start = Number((value as any).start);
        const end = Number((value as any).end);
        if (!isNaN(start) && !isNaN(end)) {
          const startTime = secondsToTime(start);
          const endTime = secondsToTime(end);
          urlParams.set(urlKey, `${startTime},${endTime}`);
        }
      }
    } else if (filterConfig.type === 'numberRange') {
      if (value && typeof value === 'object' && 'from' in value && 'to' in value) {
        const start = Number((value as any).from);
        const end = Number((value as any).to);
        if (
          !isNaN(start) &&
          !isNaN(end) &&
          (value as any).from !== '' &&
          (value as any).to !== ''
        ) {
          urlParams.set(urlKey, `${start},${end}`);
        }
      }
    } else if (filterConfig.type === 'boolean') {
      if (typeof value === 'boolean') {
        urlParams.set(urlKey, value.toString());
      }
    } else if (filterConfig.type === 'position') {
      if (value && typeof value === 'object' && 'latitude' in value && 'longitude' in value) {
        const lat = Number((value as any).latitude);
        const lon = Number((value as any).longitude);
        if (!isNaN(lat) && !isNaN(lon)) {
          urlParams.set(urlKey, `${lat},${lon}`);
        }
      }
    }
  });

  const queryString = urlParams.toString();

  if (humanReadable) {
    // Decode URL-encoded characters for human readability
    return decodeURIComponent(queryString);
  }

  return queryString;
};

export const useParseUrlFilters = <T extends Record<string, any>>(
  filterKeys: FilterKeyConfig[]
): T => {
  const memoizedFilterKeys = useMemo(() => filterKeys, [JSON.stringify(filterKeys)]);

  const [filters] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return {} as T;
    }
    return parseUrlFilters<T>(memoizedFilterKeys);
  });

  return filters;
};
